import { axiosInstance } from '@/plugins/axios';
import { handleFailedNetworkResponse } from '../helpers';
import { validateString } from '../validations';

const STATUS_VERIFIED = 'VERIFIED';
const HIGHLIGHT_TIME_FOR_RESEND_MS = 5000;
const DEFAULT_DAYS_AHEAD_FOR_EXPIRATION = 2;
const SHOW_TOAST_ERROR_ACTION = 'toastr/error';
const POLLING_INTERVAL_MS = 30 * 1000; // 30 seconds

export const moduleState = {
  recipients: null,
  loadingRecipients: false,
  loadingBalances: false,
  error: null,
  recentlyResentRequestId: null,
  recentlyResentTimestamp: null,
  pollingIntervalId: null,
  lastPolledTimestamp: null,
  hasAcceptanceChanges: false,
};

export const getters = {
  recipients: state => state.recipients,
  loadingRecipients: state => state.loadingRecipients,
  loadingBalances: state => state.loadingBalances,
  error: state => state.error,
  verifiedRecipients: state =>
    state.recipients?.filter(recipient => recipient.status === STATUS_VERIFIED) || [],
  pendingRecipients: state =>
    state.recipients?.filter(recipient => recipient.status !== STATUS_VERIFIED) || [],
  isRequestRecentlyResent: state => requestId => {
    if (state.recentlyResentRequestId !== requestId || !state.recentlyResentTimestamp) {
      return false;
    }
    const now = Date.now();
    const elapsed = now - state.recentlyResentTimestamp;
    return elapsed < HIGHLIGHT_TIME_FOR_RESEND_MS;
  },
  hasAcceptanceChanges: state => state.hasAcceptanceChanges,
  isPolling: state => state.pollingIntervalId !== null,
};

const eligibleStatuses = {
  ACTIVE: true,
  PAUSE_SCHED: true,
  PAUSE_VAC: true,
  PENDING_VAL: true,
  'CASH CAP': true,
};

// Helper function to determine if a recipient is eligible for funding
const isEligibleForFunding = status => eligibleStatuses[status?.toUpperCase()] ?? false;

export const mutations = {
  SET_RECIPIENTS(state, recipients) {
    // Create a map of existing recipients by their ID for quick lookup
    const existingRecipientsMap = new Map();
    if (state.recipients) {
      state.recipients.forEach(recipient => {
        const id = recipient.fundingRecipientClientId || recipient.id;
        if (id) {
          existingRecipientsMap.set(id, recipient);
        }
      });
    }

    // Add isEligibleForFunding property to each recipient
    // Preserve existing balance if incoming balance is null
    state.recipients = recipients.map(recipient => {
      const id = recipient.fundingRecipientClientId || recipient.id;
      const existingRecipient = existingRecipientsMap.get(id);

      return {
        ...recipient,
        // Preserve existing balance if new balance is null
        webLeadsBalance:
          recipient.webLeadsBalance !== null
            ? recipient.webLeadsBalance
            : (existingRecipient?.webLeadsBalance ?? null),
        isEligibleForFunding: isEligibleForFunding(recipient.fundingRecipientClientStatus),
      };
    });
  },
  REMOVE_RECIPIENT(state, fundingRecipientClientId) {
    state.recipients = state.recipients.filter(
      recipient => recipient.fundingRecipientClientId !== fundingRecipientClientId
    );
  },
  REMOVE_REQUEST(state, requestId) {
    state.recipients = state.recipients.filter(recipient => recipient.id !== requestId);
  },
  UPDATE_REQUEST_EXPIRATION(state, { requestId, newExpirationUtc }) {
    const request = state.recipients.find(recipient => recipient.id === requestId);
    if (request) {
      request.requestExpirationUtc = newExpirationUtc;
    }
  },
  SET_LOADING_RECIPIENTS(state, loading) {
    state.loadingRecipients = loading;
  },
  SET_LOADING_BALANCES(state, loading) {
    state.loadingBalances = loading;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  CLEAR_ERROR(state) {
    state.error = null;
  },
  SET_RECENTLY_RESENT_REQUEST(state, requestId) {
    state.recentlyResentRequestId = requestId;
    state.recentlyResentTimestamp = Date.now();
  },
  CLEAR_RECENTLY_RESENT_REQUEST(state) {
    state.recentlyResentRequestId = null;
    state.recentlyResentTimestamp = null;
  },
  SET_POLLING_INTERVAL_ID(state, intervalId) {
    state.pollingIntervalId = intervalId;
  },
  SET_LAST_POLLED_TIMESTAMP(state, timestamp) {
    state.lastPolledTimestamp = timestamp;
  },
  SET_HAS_ACCEPTANCE_CHANGES(state, hasChanges) {
    state.hasAcceptanceChanges = hasChanges;
  },
};

export const actions = {
  async getBulkFundingRecipients({ commit }, { clientId, includeBalances = true }) {
    const loadingMutation = includeBalances ? 'SET_LOADING_BALANCES' : 'SET_LOADING_RECIPIENTS';

    try {
      commit(loadingMutation, true);
      commit('CLEAR_ERROR');

      const response = await axiosInstance.get(`/api/bulkFunding/manager/${clientId}/recipients`, {
        params: { includeBalances },
      });

      commit('SET_RECIPIENTS', response.data);
      commit(loadingMutation, false);

      return response.data;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to fetch bulk funding recipients');
      commit('SET_LOADING_RECIPIENTS', false);
      commit('SET_LOADING_BALANCES', false);
      handleFailedNetworkResponse(this.dispatch, error);
      throw error;
    }
  },

  async refreshRecipients({ dispatch }, { clientId }) {
    // Fire fast request first (without balances)
    await dispatch('getBulkFundingRecipients', {
      clientId,
      includeBalances: false,
    });

    // Fire slow request in background (with balances)
    dispatch('getBulkFundingRecipients', { clientId, includeBalances: true });
  },

  async deleteRecipient(
    { commit, dispatch },
    { fundingManagerClientId, fundingRecipientClientId }
  ) {
    // Validate required parameters - let exceptions bubble up for logging
    validateString(fundingManagerClientId, 'fundingManagerClientId');
    validateString(fundingRecipientClientId, 'fundingRecipientClientId');

    try {
      commit('SET_LOADING_RECIPIENTS', true);
      commit('CLEAR_ERROR');

      // Optimistically remove the recipient from the UI
      commit('REMOVE_RECIPIENT', fundingRecipientClientId);

      await axiosInstance.delete(
        `/api/BulkFunding/manager/${fundingManagerClientId}/recipients/${fundingRecipientClientId}`
      );

      // Fire-and-forget refresh to ensure consistency with server
      dispatch('refreshRecipients', {
        clientId: fundingManagerClientId,
      });

      return { success: true };
    } catch (error) {
      // Fire-and-forget refresh to restore the recipient if deletion failed
      dispatch('refreshRecipients', {
        clientId: fundingManagerClientId,
      });

      commit('SET_ERROR', error.message || 'Failed to delete recipient');
      commit('SET_LOADING_RECIPIENTS', false);
      handleFailedNetworkResponse(this.dispatch, error);
      dispatch(SHOW_TOAST_ERROR_ACTION, 'Failed to delete recipient', {
        root: true,
      });
      return { success: false };
    }
  },

  async deleteRequest({ commit, dispatch }, { fundingManagerClientId, requestId }) {
    // Validate required parameters - let exceptions bubble up for logging
    validateString(fundingManagerClientId, 'fundingManagerClientId');
    validateString(requestId, 'requestId');

    try {
      commit('SET_LOADING_RECIPIENTS', true);
      commit('CLEAR_ERROR');

      // Optimistically remove the request from the UI
      commit('REMOVE_REQUEST', requestId);

      await axiosInstance.delete(
        `/api/BulkFunding/manager/${fundingManagerClientId}/requests/${requestId}`
      );

      // Fire-and-forget refresh to ensure consistency with server
      dispatch('refreshRecipients', {
        clientId: fundingManagerClientId,
      });

      return { success: true };
    } catch (error) {
      // Fire-and-forget refresh to restore the request if deletion failed
      dispatch('refreshRecipients', {
        clientId: fundingManagerClientId,
      });

      commit('SET_ERROR', error.message || 'Failed to delete request');
      commit('SET_LOADING_RECIPIENTS', false);
      handleFailedNetworkResponse(this.dispatch, error);
      dispatch(SHOW_TOAST_ERROR_ACTION, 'Failed to delete request', {
        root: true,
      });
      return { success: false };
    }
  },

  async resendRequest({ commit, dispatch }, { fundingManagerClientId, email, requestId }) {
    // Validate required parameters - let exceptions bubble up for logging
    validateString(fundingManagerClientId, 'fundingManagerClientId');
    validateString(email, 'email');
    validateString(requestId, 'requestId');

    try {
      commit('SET_LOADING_RECIPIENTS', true);
      commit('CLEAR_ERROR');

      // Optimistically update the expiration date (set to 2 days from now as a placeholder)
      const optimisticExpiration = new Date();
      optimisticExpiration.setDate(
        optimisticExpiration.getDate() + DEFAULT_DAYS_AHEAD_FOR_EXPIRATION
      ); // Set to 2 days from now as a placeholder
      commit('UPDATE_REQUEST_EXPIRATION', {
        requestId,
        newExpirationUtc: optimisticExpiration.toISOString(),
      });

      // Mark this request as recently resent for visual feedback
      commit('SET_RECENTLY_RESENT_REQUEST', requestId);

      // Clear the highlight after 5 seconds
      setTimeout(() => {
        commit('CLEAR_RECENTLY_RESENT_REQUEST');
      }, HIGHLIGHT_TIME_FOR_RESEND_MS); // Clear the highlight after 5 seconds

      await axiosInstance.post(`/api/bulkFunding/manager/${fundingManagerClientId}/recipients`, {
        emails: [email],
      });

      // Fire-and-forget refresh to get the actual expiration date from server
      dispatch('refreshRecipients', {
        clientId: fundingManagerClientId,
      });

      return { success: true };
    } catch (error) {
      // Clear the highlight on error
      commit('CLEAR_RECENTLY_RESENT_REQUEST');

      // Fire-and-forget refresh to restore the original expiration if resend failed
      dispatch('refreshRecipients', {
        clientId: fundingManagerClientId,
      });

      commit('SET_ERROR', error.message || 'Failed to resend request');
      commit('SET_LOADING_RECIPIENTS', false);
      handleFailedNetworkResponse(this.dispatch, error);
      dispatch(SHOW_TOAST_ERROR_ACTION, 'Failed to resend request', {
        root: true,
      });
      return { success: false };
    }
  },

  async checkForRecipientChanges({ commit, state }, { clientId }) {
    try {
      // Use last polled timestamp, or default to 5 minutes ago if this is the first check
      const startFromDateTimeUtc =
        state.lastPolledTimestamp || new Date(Date.now() - POLLING_INTERVAL_MS).toISOString();

      const response = await axiosInstance.get(
        `/api/bulkFunding/manager/${clientId}/recipients/changes`,
        {
          params: { startFromDateTimeUtc },
        }
      );

      commit('SET_LAST_POLLED_TIMESTAMP', new Date().toISOString());

      if (response.data?.hasChanges) {
        commit('SET_HAS_ACCEPTANCE_CHANGES', true);
      }

      return response.data;
    } catch (error) {
      // Silently fail - don't disrupt the user experience for polling errors
      // console.error('Failed to check for recipient changes:', error);
      return { hasChanges: false };
    }
  },

  startPollingForChanges({ commit, dispatch, state }, { clientId }) {
    // Don't start if already polling
    if (state.pollingIntervalId) {
      return;
    }

    // Kick off polling
    commit('SET_LAST_POLLED_TIMESTAMP', new Date().toISOString());
    const intervalId = setInterval(() => {
      dispatch('checkForRecipientChanges', { clientId });
    }, POLLING_INTERVAL_MS);

    commit('SET_POLLING_INTERVAL_ID', intervalId);
  },

  stopPollingForChanges({ commit, state }) {
    if (state.pollingIntervalId) {
      clearInterval(state.pollingIntervalId);
      commit('SET_POLLING_INTERVAL_ID', null);
    }
  },

  clearAcceptanceChanges({ commit }) {
    commit('SET_HAS_ACCEPTANCE_CHANGES', false);
  },

  async refreshForAcceptanceChanges({ commit, dispatch }, { clientId }) {
    // Clear the changes flag
    commit('SET_HAS_ACCEPTANCE_CHANGES', false);

    // Stop polling temporarily
    dispatch('stopPollingForChanges');

    // Reload recipients to get the updated list
    await dispatch('refreshRecipients', { clientId });

    // Note: Caller should restart polling if needed based on pendingRecipients count
  },

  clearError({ commit }) {
    commit('CLEAR_ERROR');
  },

  reset({ commit, dispatch }) {
    dispatch('stopPollingForChanges');
    commit('SET_RECIPIENTS', []);
    commit('SET_LOADING_RECIPIENTS', false);
    commit('SET_LOADING_BALANCES', false);
    commit('CLEAR_ERROR');
    commit('SET_HAS_ACCEPTANCE_CHANGES', false);
    commit('SET_LAST_POLLED_TIMESTAMP', null);
  },
};
